import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { Link, useNavigate } from 'react-router-dom';

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleCategoryClick = (link: string) => {
    setDropdownOpen(false);
    setMobileOpen(false);
    window.open(link, '_blank', 'noopener=yes,noreferrer=yes');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              Kakobuy<span className="text-primary-500">Finds</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
              {t('nav.home')}
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer bg-background-100 hover:bg-background-200 px-4 py-2 rounded-full"
              >
                <i className="ri-grid-line w-4 h-4 flex items-center justify-center"></i>
                <span>{t('nav.categories')}</span>
                {dropdownOpen ? (
                  <i className="ri-arrow-up-s-line w-3 h-3 flex items-center justify-center"></i>
                ) : (
                  <i className="ri-arrow-down-s-line w-3 h-3 flex items-center justify-center"></i>
                )}
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-[720px] bg-background-50 rounded-xl border border-background-200/70 p-4 grid grid-cols-2 gap-1 max-h-[420px] overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategoryClick(cat.link)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent-50 transition-colors text-left group cursor-pointer"
                    >
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100/70 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-all">
                        <i className={`${cat.icon} text-base`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground-900 group-hover:text-accent-700 transition-colors whitespace-nowrap">
                          {cat.namePl}
                        </p>
                      </div>
                      <i className="ri-arrow-right-up-line text-xs text-foreground-400 group-hover:text-accent-500 transition-colors"></i>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => navigate('/artykul/kakobuy-spreadsheet-kompletny-przewodnik')}
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav.articles')}
            </button>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('hero.cta.primary')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold px-5 py-2.5 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              {t('hero.cta.secondary')}
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            aria-label={mobileOpen ? 'Zamknij menu' : 'Otwórz menu'}
          >
            {mobileOpen ? (
              <i className="ri-close-line text-xl text-foreground-900"></i>
            ) : (
              <i className="ri-menu-line text-xl text-foreground-900"></i>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 top-16 md:hidden z-40">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-foreground-950/30 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          ></div>

          {/* Menu Panel */}
          <div className="relative bg-background-50 h-full max-h-[calc(100vh-64px)] overflow-y-auto flex flex-col">
            {/* Quick Actions */}
            <div className="px-5 py-4 flex items-center gap-3 border-b border-background-200/60">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex-1 text-center text-sm font-semibold px-5 py-3 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                {t('hero.cta.secondary')}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex-1 text-center text-sm font-semibold px-5 py-3 rounded-full border-2 border-foreground-300 text-foreground-700 hover:bg-foreground-50 transition-all whitespace-nowrap cursor-pointer"
              >
                {t('hero.cta.primary')}
              </a>
            </div>

            {/* Categories Header */}
            <div className="px-5 pt-5 pb-2">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className="ri-grid-line text-sm"></i>
                </div>
                <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                  {t('nav.categories')}
                </span>
              </div>
              <p className="text-[11px] text-foreground-400 ml-8">
                Wybierz kategorię, aby przejść do arkusza Kakobuy
              </p>
            </div>

            {/* Categories Grid - 2 columns on mobile */}
            <div className="px-3 py-2 grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => handleCategoryClick(cat.link)}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-accent-50 active:bg-accent-100 transition-colors cursor-pointer text-left"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100/70 text-accent-600 flex-shrink-0">
                    <i className={`${cat.icon} text-lg`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground-900 whitespace-nowrap">
                      {cat.namePl}
                    </p>
                    <p className="text-[11px] text-foreground-400 leading-tight mt-0.5 line-clamp-1">
                      {cat.description}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            {/* Divider */}
            <div className="mx-5 my-3 border-t border-background-200/60"></div>

            {/* Navigation Links */}
            <div className="px-5 pb-2">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3 px-2">
                Nawigacja
              </p>
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-100 text-foreground-600">
                  <i className="ri-home-4-line text-lg"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-900">{t('nav.home')}</span>
              </Link>

              <button
                onClick={() => {
                  setMobileOpen(false);
                  navigate('/artykul/kakobuy-spreadsheet-kompletny-przewodnik');
                }}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer w-full text-left"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-100 text-foreground-600">
                  <i className="ri-article-line text-lg"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-900">Kompletny Przewodnik</span>
              </button>

              <button
                onClick={() => {
                  setMobileOpen(false);
                  navigate('/artykul/kakobuy-vs-cssbuy-vs-superbuy-porownanie');
                }}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer w-full text-left"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-100 text-foreground-600">
                  <i className="ri-scales-3-line text-lg"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-900">Porównanie Arkuszy</span>
              </button>

              <button
                onClick={() => {
                  setMobileOpen(false);
                  navigate('/artykul/kakobuy-spreadsheet-bezpieczenstwo-zakupow');
                }}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer w-full text-left"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-100 text-foreground-600">
                  <i className="ri-shield-check-line text-lg"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-900">Bezpieczeństwo Zakupów</span>
              </button>

              <button
                onClick={() => {
                  setMobileOpen(false);
                  navigate('/artykul/najlepsze-znaleziska-kakobuy-spreadsheet-2026');
                }}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer w-full text-left"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-100 text-foreground-600">
                  <i className="ri-star-line text-lg"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-900">Top 10 Znalezisk 2026</span>
              </button>
            </div>

            {/* Bottom spacer */}
            <div className="pb-8"></div>
          </div>
        </div>
      )}
    </nav>
  );
}